import { TableDG } from '@alison/material';
import Box from '@mui/material/Box';
import type { GridColDef } from '@mui/x-data-grid';
import { type ReactNode, useMemo } from 'react';
import { TableEmptyState } from '../../creatives/TableEmptyState';
import { GRID_CONTAINER_SX, GRID_SX_BASE, NO_CREATIVES_MESSAGE } from '../../creatives/utils';
import {
  type CompetitionCreativeGridRow,
  CompetitorCell,
  CreativeCell,
  DateAddedCell,
  DaysRunningCell,
  LengthCell,
  TaggingCell,
  toGridRow,
} from './CompetitionCreativesTableCells';
import type { CompetitionCreativesData } from './data/api';

const DATA_ROW_HEIGHT = 76;
const CREATIVE_COLUMN_MIN_WIDTH = 340;
const CREATIVE_COLUMN_FLEX = 3;
const METRIC_COLUMN_WIDTH = 140;
// Wider than the other metric columns: "Jun 28, 2026" no longer fits the shared 140px floor now
// that dates always include a year.
const DATE_COLUMN_MIN_WIDTH = 160;

interface CompetitionCreativesTableProps {
  data: CompetitionCreativesData | undefined;
  pending: boolean;
  onOpen: (creativeId: string) => void;
}

export function CompetitionCreativesTable({
  data,
  pending,
  onOpen,
}: CompetitionCreativesTableProps): ReactNode {
  const creatives = useMemo(() => data?.creatives ?? [], [data]);

  const columns = useMemo<GridColDef<CompetitionCreativeGridRow>[]>(
    () => [
      {
        field: 'creative',
        headerName: 'CREATIVE',
        minWidth: CREATIVE_COLUMN_MIN_WIDTH,
        flex: CREATIVE_COLUMN_FLEX,
        renderCell: CreativeCell,
        valueGetter: (_value, row) => row.creative.creativeId,
      },
      {
        field: 'competitor',
        headerName: 'COMPETITOR',
        minWidth: 220,
        renderCell: CompetitorCell,
        valueGetter: (_value, row) => row.creative.competitorName ?? '',
      },
      {
        field: 'activeDays',
        headerName: 'DAYS RUNNING',
        minWidth: METRIC_COLUMN_WIDTH,
        align: 'center',
        headerAlign: 'center',
        type: 'number',
        renderCell: DaysRunningCell,
        valueGetter: (_value, row) => row.creative.activeDays,
      },
      {
        field: 'duration',
        headerName: 'LENGTH',
        minWidth: METRIC_COLUMN_WIDTH,
        align: 'center',
        headerAlign: 'center',
        type: 'number',
        renderCell: LengthCell,
        valueGetter: (_value, row) => row.creative.duration,
      },
      {
        field: 'firstSeenDate',
        headerName: 'DATE ADDED',
        minWidth: DATE_COLUMN_MIN_WIDTH,
        align: 'center',
        headerAlign: 'center',
        renderCell: DateAddedCell,
        valueGetter: (_value, row) => row.creative.firstSeenDate ?? '',
      },
      {
        field: 'tagged',
        headerName: 'TAGGING',
        minWidth: METRIC_COLUMN_WIDTH,
        align: 'center',
        headerAlign: 'center',
        type: 'boolean',
        renderCell: TaggingCell,
        valueGetter: (_value, row) => row.creative.tagged,
      },
    ],
    [],
  );

  const rows = useMemo<CompetitionCreativeGridRow[]>(() => creatives.map(toGridRow), [creatives]);

  return (
    <Box>
      <Box sx={GRID_CONTAINER_SX}>
        <TableDG
          columns={columns}
          rows={rows}
          loading={pending}
          disableColumnMenu
          disableRowSelectionOnClick
          getRowHeight={() => DATA_ROW_HEIGHT}
          onRowClick={(params) => onOpen(params.row.creative.creativeId)}
          hideFooter
          sx={(theme) => ({ ...GRID_SX_BASE(theme), '& .MuiDataGrid-row': { cursor: 'pointer' } })}
          slots={{ noRowsOverlay: TableEmptyState }}
          slotProps={{ noRowsOverlay: { children: NO_CREATIVES_MESSAGE } }}
        />
      </Box>
    </Box>
  );
}
